import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  AppShell,
  Container,
  Title,
  Group,
  Button,
  Stack,
  Loader,
  Center,
  ActionIcon,
  Tooltip,
  Text,
  Badge,
  Box,
  Select,
} from '@mantine/core';
import { IconSettings, IconRefresh, IconFileText } from '@tabler/icons-react';
import { useAuth } from './hooks/useAuth';
import { useShipments, type ShipmentFilter } from './hooks/useShipments';
import { apiFetch } from './lib/api';
import { useDeliveredShipments, type DateRangePreset } from './hooks/useDeliveredShipments';
// Stats are now computed from loaded shipments, not from pre-computed Firestore docs
import { StatsHeader } from './components/StatsHeader';
import { MarketTabs } from './components/MarketTabs';
import { ShipmentTable } from './components/ShipmentTable';
import { ShipmentDetail } from './components/ShipmentDetail';
import { LoginForm } from './components/LoginForm';
import { NotificationCenter } from './components/NotificationCenter';
import { HolidaySettings } from './components/HolidaySettings';
import { ReportLog } from './components/ReportLog';
import type { ViewMode } from './components/ViewToggle';
import type { Market, Shipment } from './types';

// URL path to filter mapping
const URL_TO_FILTER: Record<string, ShipmentFilter> = {
  'active': 'active',
  'awaiting': 'awaiting_fulfillment',
  'in-transit': 'in_transit',
  'at-risk': 'at_risk',
  'needs-action': 'needs_action',
  'deliver-today': 'deliver_today',
  'delivered/today': 'delivered_today',
  'delivered/yesterday': 'delivered_yesterday',
  'exceptions': 'exceptions',
};

// Filter to URL path mapping (reverse)
const FILTER_TO_URL: Record<ShipmentFilter, string> = {
  'active': 'active',
  'awaiting_fulfillment': 'awaiting',
  'in_transit': 'in-transit',
  'at_risk': 'at-risk',
  'needs_action': 'needs-action',
  'deliver_today': 'deliver-today',
  'delivered_today': 'delivered/today',
  'delivered_yesterday': 'delivered/yesterday',
  'exceptions': 'exceptions',
};

// URL paths for Delivered view date ranges
const URL_TO_DATE_PRESET: Record<string, DateRangePreset> = {
  // Hour-based
  'delivered/6h': '6h',
  'delivered/12h': '12h',
  'delivered/24h': '24h',
  'delivered/48h': '48h',
  // Day-based
  'delivered/today': 'today',
  'delivered/yesterday': 'yesterday',
  // Week-based
  'delivered/this-week': 'this_week',
  'delivered/last-week': 'last_week',
  'delivered/last-2-weeks': 'last_2_weeks',
  'delivered/last-3-weeks': 'last_3_weeks',
  // Legacy
  'delivered/7d': '7d',
  'delivered/14d': '14d',
  'delivered/30d': '30d',
};

const DATE_PRESET_TO_URL: Record<DateRangePreset, string> = {
  // Hour-based
  '6h': 'delivered/6h',
  '12h': 'delivered/12h',
  '24h': 'delivered/24h',
  '48h': 'delivered/48h',
  // Day-based
  'today': 'delivered/today',
  'yesterday': 'delivered/yesterday',
  // Week-based
  'this_week': 'delivered/this-week',
  'last_week': 'delivered/last-week',
  'last_2_weeks': 'delivered/last-2-weeks',
  'last_3_weeks': 'delivered/last-3-weeks',
  // Legacy
  '7d': 'delivered/7d',
  '14d': 'delivered/14d',
  '30d': 'delivered/30d',
  'custom': 'delivered/today', // Fallback for custom
};

// Base path for the operations/shipments app
const BASE_PATH = '/operations/shipments';

// API URL for sync endpoint (empty string = same-origin via Firebase proxy)
const API_BASE_URL = import.meta.env.VITE_API_URL ?? '';

// Sync interval (5 minutes)
const SYNC_INTERVAL_MS = 5 * 60 * 1000;

// Parse shipment ID from URL path (e.g., /operations/shipments/123456)
// Returns null if path matches a filter pattern instead
function getShipmentIdFromUrl(): string | null {
  const match = window.location.pathname.match(/^\/operations\/shipments\/(\d+)$/);
  return match ? match[1] : null;
}

// Parse filter from URL path (e.g., /operations/shipments/awaiting, /operations/shipments/delivered/today)
// Returns null if path is a shipment ID or unrecognized
function getFilterFromUrl(): ShipmentFilter | null {
  const pathname = window.location.pathname;

  // Check for filter paths (order matters - check longer paths first)
  for (const [urlPath, filter] of Object.entries(URL_TO_FILTER)) {
    if (pathname === `${BASE_PATH}/${urlPath}`) {
      return filter;
    }
  }

  return null;
}

// Parse view mode and date preset from URL (for Complete view)
function getViewModeFromUrl(): { viewMode: ViewMode; datePreset: DateRangePreset } | null {
  const pathname = window.location.pathname;

  // Check for complete view paths
  for (const [urlPath, preset] of Object.entries(URL_TO_DATE_PRESET)) {
    if (pathname === `${BASE_PATH}/${urlPath}`) {
      return { viewMode: 'complete', datePreset: preset };
    }
  }

  // Check for exceptions view
  if (pathname === `${BASE_PATH}/exceptions`) {
    return { viewMode: 'exceptions', datePreset: 'today' };
  }

  // Check if it's any inflight filter path or base path
  if (pathname === BASE_PATH || getFilterFromUrl() !== null) {
    return { viewMode: 'inflight', datePreset: 'today' };
  }

  return null;
}

// Update URL for view mode change
function updateViewModeUrl(viewMode: ViewMode, filter?: ShipmentFilter, datePreset?: DateRangePreset) {
  let newPath: string;

  if (viewMode === 'complete') {
    newPath = `${BASE_PATH}/${DATE_PRESET_TO_URL[datePreset || '7d']}`;
  } else if (viewMode === 'exceptions') {
    newPath = `${BASE_PATH}/exceptions`;
  } else {
    // Inflight view
    newPath = filter === 'active' ? BASE_PATH : `${BASE_PATH}/${FILTER_TO_URL[filter || 'active']}`;
  }

  if (window.location.pathname !== newPath) {
    window.history.pushState({ viewMode, filter, datePreset }, '', newPath);
  }
}

// Update URL to reflect selected shipment (clears filter path)
function updateShipmentUrl(shipmentId: string | null, currentFilter: ShipmentFilter) {
  let newPath: string;
  if (shipmentId) {
    newPath = `${BASE_PATH}/${shipmentId}`;
  } else {
    // When closing detail, return to filter path (or base path for 'active')
    newPath = currentFilter === 'active' ? BASE_PATH : `${BASE_PATH}/${FILTER_TO_URL[currentFilter]}`;
  }

  if (window.location.pathname !== newPath) {
    window.history.pushState({ shipmentId, filter: currentFilter }, '', newPath);
  }
}

// Redirect root or /operations to /operations/shipments
function ensureShipmentsPath() {
  const pathname = window.location.pathname;
  if (pathname === '/' || pathname === '' || pathname === '/operations' || pathname === '/operations/') {
    window.history.replaceState({}, '', BASE_PATH);
  }
}

function Dashboard() {
  const [market, setMarket] = useState<Market | 'all'>('all');
  // Initialize view mode and filter from URL
  const [viewMode, setViewMode] = useState<ViewMode>(() => getViewModeFromUrl()?.viewMode ?? 'inflight');
  const [filter, setFilter] = useState<ShipmentFilter>(() => getFilterFromUrl() ?? 'active');
  const [datePreset, setDatePreset] = useState<DateRangePreset>(() => getViewModeFromUrl()?.datePreset ?? 'today');
  const [selectedShipment, setSelectedShipment] = useState<Shipment | null>(null);
  const [detailOpened, setDetailOpened] = useState(false);
  const [pendingShipmentId, setPendingShipmentId] = useState<string | null>(() => getShipmentIdFromUrl());
  const [settingsOpened, setSettingsOpened] = useState(false);
  const [reportsOpened, setReportsOpened] = useState(false);

  // Sync state for polling mechanism
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState<Date | null>(null);
  const [syncError, setSyncError] = useState<string | null>(null);
  const syncIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const syncingRef = useRef(false);

  // Trigger sync with backend
  const triggerSync = useCallback(async () => {
    if (syncingRef.current) return; // Prevent concurrent syncs

    syncingRef.current = true;
    setSyncing(true);
    setSyncError(null);

    try {
      const response = await apiFetch(`${API_BASE_URL}/api/shipments/sync`, {
        method: 'POST',
      });

      if (!response.ok) {
        throw new Error(`Sync failed: ${response.statusText}`);
      }

      const result = await response.json();
      console.log('[SYNC] Completed:', result);
      setLastSync(new Date());
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Sync failed';
      console.error('[SYNC] Error:', message);
      setSyncError(message);
    } finally {
      syncingRef.current = false;
      setSyncing(false);
    }
  }, []);

  // Set up polling interval
  useEffect(() => {
    // Initial sync on mount
    triggerSync();

    // Set up 5-minute interval
    syncIntervalRef.current = setInterval(() => {
      triggerSync();
    }, SYNC_INTERVAL_MS);

    return () => {
      if (syncIntervalRef.current) {
        clearInterval(syncIntervalRef.current);
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Redirect root to /shipments on mount
  useEffect(() => {
    ensureShipmentsPath();
  }, []);

  // Handle view mode change
  const handleViewModeChange = useCallback((newViewMode: ViewMode) => {
    setViewMode(newViewMode);
    setDetailOpened(false); // Close detail pane when changing view
    updateViewModeUrl(newViewMode, filter, datePreset);
  }, [filter, datePreset]);

  // Handle date preset change
  const handleDatePresetChange = useCallback((newPreset: string | null) => {
    if (newPreset) {
      setDatePreset(newPreset as DateRangePreset);
      updateViewModeUrl('complete', filter, newPreset as DateRangePreset);
    }
  }, [filter]);

  // Handle inflight filter change
  const handleInflightFilterChange = useCallback((newFilter: string | null) => {
    if (newFilter) {
      setFilter(newFilter as ShipmentFilter);
      setDetailOpened(false);
      updateViewModeUrl('inflight', newFilter as ShipmentFilter, datePreset);
    }
  }, [datePreset]);

  // Navigate to Complete view with a specific date preset (from stats cards)
  const handleNavigateToComplete = useCallback((preset: DateRangePreset) => {
    setViewMode('complete');
    setDatePreset(preset);
    setDetailOpened(false);
    updateViewModeUrl('complete', filter, preset);
  }, [filter]);

  // Fetch shipments - use different hooks based on view mode
  // Note: We fetch 'active' filter to get all non-delivered for stats calculation
  const { shipments: inflightShipments, loading: inflightLoading } = useShipments('all', filter);
  const { shipments: allActiveShipments, loading: allActiveLoading } = useShipments('all', 'active');
  const { shipments: exceptionShipments, loading: exceptionsLoading } = useShipments('all', 'exceptions');
  const { shipments: deliveredShipments, loading: deliveredLoading } = useDeliveredShipments('all', datePreset);
  const { shipments: deliveredThisWeekShipments } = useDeliveredShipments('all', 'this_week'); // For stats
  const { signOut } = useAuth();

  // Compute stats from loaded shipments (real-time updates)
  const computedStats = useMemo(() => {
    if (allActiveLoading) return null;

    // Filter by market for stats
    const marketShipments = market === 'all'
      ? allActiveShipments
      : allActiveShipments.filter(s => s.market === market);

    const marketDelivered = market === 'all'
      ? deliveredThisWeekShipments
      : deliveredThisWeekShipments.filter(s => s.market === market);

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    // Get start of week (Monday)
    const startOfWeek = new Date(today);
    const dayOfWeek = startOfWeek.getDay();
    const diff = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday = 1
    startOfWeek.setDate(startOfWeek.getDate() + diff);

    // Calculate avg transit from delivered shipments with time_in_transit_hours
    const transitHours = marketDelivered
      .filter(s => s.time_in_transit_hours != null && s.time_in_transit_hours > 0)
      .map(s => s.time_in_transit_hours as number);
    const avgTransit = transitHours.length > 0
      ? transitHours.reduce((a, b) => a + b, 0) / transitHours.length
      : 0;

    // Calculate transit day buckets for in-transit shipments based on estimated transit_days
    const inTransitShipments = marketShipments.filter(s =>
      ['picked_up', 'in_transit', 'out_for_delivery'].includes(s.status)
    );

    const transit0d = inTransitShipments.filter(s => s.transit_days === 0).length;
    const transit1d = inTransitShipments.filter(s => s.transit_days === 1).length;
    const transit2dPlus = inTransitShipments.filter(s => s.transit_days >= 2).length;

    return {
      awaiting_fulfillment: marketShipments.filter(s =>
        s.status === 'awaiting_fulfillment' || s.status === 'pending'
      ).length,
      in_transit: inTransitShipments.length,
      out_for_delivery: marketShipments.filter(s => s.status === 'out_for_delivery').length,
      transit_0d: transit0d,
      transit_1d: transit1d,
      transit_2d_plus: transit2dPlus,
      at_risk: marketShipments.filter(s =>
        s.risk_level === 'high' || s.risk_level === 'critical'
      ).length,
      needs_action: marketShipments.filter(s => s.needs_action).length,
      exceptions: marketShipments.filter(s => s.status === 'exception').length,
      delivered_today: marketDelivered.filter(s => {
        if (!s.delivered_at) return false;
        const d = new Date(s.delivered_at);
        return d >= today;
      }).length,
      delivered_yesterday: marketDelivered.filter(s => {
        if (!s.delivered_at) return false;
        const d = new Date(s.delivered_at);
        return d >= yesterday && d < today;
      }).length,
      delivered_this_week: marketDelivered.filter(s => {
        if (!s.delivered_at) return false;
        const d = new Date(s.delivered_at);
        return d >= startOfWeek;
      }).length,
      total_active: marketShipments.length,
      avg_transit_hours_7d: avgTransit,
      on_time_rate_7d: 0, // Would need deadline comparison
      updated_at: new Date(),
    };
  }, [allActiveShipments, deliveredThisWeekShipments, market, allActiveLoading]);

  const statsLoading = allActiveLoading;

  // Select data based on view mode
  const allShipments = viewMode === 'complete'
    ? deliveredShipments
    : viewMode === 'exceptions'
      ? exceptionShipments
      : inflightShipments.filter(s => s.status !== 'exception');
  const shipmentsLoading = viewMode === 'complete'
    ? deliveredLoading
    : viewMode === 'exceptions'
      ? exceptionsLoading
      : inflightLoading;

  // Filter shipments by selected market
  const shipments = market === 'all'
    ? allShipments
    : allShipments.filter(s => s.market === market);

  // Calculate counts for tabs (based on current view)
  const counts = {
    all: allShipments.length,
    canada: allShipments.filter(s => s.market === 'canada').length,
    usa: allShipments.filter(s => s.market === 'usa').length,
  };

  // Keep selectedShipment in sync with live Firestore data
  // Search all data sources so status changes (e.g. exception toggle) don't lose the shipment
  useEffect(() => {
    if (selectedShipment && detailOpened) {
      const updated = inflightShipments.find(s => s.id === selectedShipment.id)
        || exceptionShipments.find(s => s.id === selectedShipment.id)
        || deliveredShipments.find(s => s.id === selectedShipment.id);
      if (updated && updated !== selectedShipment) {
        setSelectedShipment(updated);
      }
    }
  }, [inflightShipments, exceptionShipments, deliveredShipments, selectedShipment, detailOpened]);

  // Open shipment from URL once shipments are loaded
  useEffect(() => {
    if (pendingShipmentId && allShipments.length > 0) {
      const shipment = allShipments.find(s => s.id === pendingShipmentId);
      if (shipment) {
        setSelectedShipment(shipment);
        setDetailOpened(true);
      }
      setPendingShipmentId(null);
    }
  }, [pendingShipmentId, allShipments]);

  // Handle browser back/forward navigation
  useEffect(() => {
    const handlePopState = (event: PopStateEvent) => {
      // Check for shipment ID first
      const shipmentId = event.state?.shipmentId || getShipmentIdFromUrl();
      if (shipmentId) {
        const shipment = allShipments.find(s => s.id === shipmentId);
        if (shipment) {
          setSelectedShipment(shipment);
          setDetailOpened(true);
        }
        // Restore filter from state if available
        if (event.state?.filter) {
          setFilter(event.state.filter);
        }
        return;
      }

      // Check for view mode (exceptions or complete)
      const viewModeFromUrl = getViewModeFromUrl();
      if (viewModeFromUrl && viewModeFromUrl.viewMode === 'exceptions') {
        setViewMode('exceptions');
        setDetailOpened(false);
        return;
      }

      // Check for filter path
      const urlFilter = getFilterFromUrl();
      if (urlFilter) {
        setFilter(urlFilter);
        setViewMode('inflight');
        setDetailOpened(false);
        return;
      }

      // Default: close detail pane, keep current filter
      setDetailOpened(false);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [allShipments]);

  const handleSelectShipment = useCallback((shipment: Shipment) => {
    setSelectedShipment(shipment);
    setDetailOpened(true);
    updateShipmentUrl(shipment.id ?? null, filter);
  }, [filter]);

  const handleCloseDetail = useCallback(() => {
    setDetailOpened(false);
    updateShipmentUrl(null, filter);
  }, [filter]);

  // Navigation helpers for detail pane
  const currentIndex = selectedShipment
    ? shipments.findIndex(s => s.id === selectedShipment.id)
    : -1;
  const hasPrev = currentIndex > 0;
  const hasNext = currentIndex >= 0 && currentIndex < shipments.length - 1;

  const handlePrevShipment = () => {
    if (hasPrev) {
      const prevShipment = shipments[currentIndex - 1];
      setSelectedShipment(prevShipment);
      updateShipmentUrl(prevShipment.id ?? null, filter);
    }
  };

  const handleNextShipment = () => {
    if (hasNext) {
      const nextShipment = shipments[currentIndex + 1];
      setSelectedShipment(nextShipment);
      updateShipmentUrl(nextShipment.id ?? null, filter);
    }
  };

  return (
    <AppShell header={{ height: 60 }} padding="md">
      <AppShell.Header>
        <Group h="100%" px="md" justify="space-between">
          <Group gap="sm">
            <img src="/logo-ts.avif" alt="Tom & Sawyer" height={36} />
            <Title order={3}>Shipment Tracker</Title>
            {lastSync && (
              <Text size="xs" c="dimmed">
                Last updated: {lastSync.toLocaleTimeString()}
              </Text>
            )}
          </Group>
          <Group gap="sm">
            {/* Sync error indicator */}
            {syncError && (
              <Tooltip label={syncError}>
                <Badge color="red" size="sm" variant="light">Sync Error</Badge>
              </Tooltip>
            )}
            <Tooltip label="Refresh data from Shopify">
              <ActionIcon
                variant="subtle"
                onClick={triggerSync}
                loading={syncing}
                disabled={syncing}
              >
                <IconRefresh size={18} />
              </ActionIcon>
            </Tooltip>
            <NotificationCenter />
            <Tooltip label="3PL Reports">
              <ActionIcon variant="subtle" onClick={() => setReportsOpened(true)}>
                <IconFileText size={20} />
              </ActionIcon>
            </Tooltip>
            <Tooltip label="Settings">
              <ActionIcon variant="subtle" onClick={() => setSettingsOpened(true)}>
                <IconSettings size={20} />
              </ActionIcon>
            </Tooltip>
            <Button variant="subtle" onClick={signOut}>
              Sign Out
            </Button>
          </Group>
        </Group>
      </AppShell.Header>

      <AppShell.Main>
        <Container fluid>
          {/* Sticky filter header */}
          <Box
            style={{
              position: 'sticky',
              top: 60, // Below AppShell.Header
              zIndex: 100,
              backgroundColor: 'var(--mantine-color-body)',
              paddingBottom: 'var(--mantine-spacing-md)',
              marginBottom: 'var(--mantine-spacing-md)',
            }}
          >
            <Stack gap="md">
              {/* Stats header - clickable blocks for view switching */}
              <StatsHeader
                stats={computedStats}
                loading={statsLoading}
                viewMode={viewMode}
                onViewModeChange={handleViewModeChange}
                onNavigateToComplete={handleNavigateToComplete}
              />

              {/* View header with count */}
              <Group justify="space-between" align="center">
                <Group gap="sm">
                  <Title order={4}>
                    {viewMode === 'complete' ? 'Delivered Shipments' : viewMode === 'exceptions' ? 'Exception Shipments' : 'Inflight Shipments'}
                  </Title>
                  <Badge size="lg" variant="light" color={viewMode === 'complete' ? 'green' : viewMode === 'exceptions' ? 'red' : 'blue'}>
                    {allShipments.length}
                  </Badge>
                </Group>
                {viewMode === 'inflight' && (
                  <Select
                    size="sm"
                    value={filter}
                    onChange={handleInflightFilterChange}
                    data={[
                      { value: 'active', label: 'All Inflight' },
                      { value: 'awaiting_fulfillment', label: 'Await Ship' },
                      { value: 'in_transit', label: 'In Transit' },
                      { value: 'deliver_today', label: 'Deliver Today' },
                      { value: 'at_risk', label: 'At Risk' },
                      { value: 'needs_action', label: 'Needs Action' },
                    ]}
                    w={150}
                  />
                )}
                {viewMode === 'complete' && (
                  <Select
                    size="sm"
                    value={datePreset}
                    onChange={handleDatePresetChange}
                    data={[
                      { group: 'Hours', items: [
                        { value: '6h', label: 'Last 6 hours' },
                        { value: '12h', label: 'Last 12 hours' },
                        { value: '24h', label: 'Last 24 hours' },
                        { value: '48h', label: 'Last 48 hours' },
                      ]},
                      { group: 'Days', items: [
                        { value: 'today', label: 'Today' },
                        { value: 'yesterday', label: 'Yesterday' },
                      ]},
                      { group: 'Weeks', items: [
                        { value: 'this_week', label: 'This week' },
                        { value: 'last_week', label: 'Last week' },
                        { value: 'last_2_weeks', label: 'Last 2 weeks' },
                        { value: 'last_3_weeks', label: 'Last 3 weeks' },
                      ]},
                    ]}
                    w={180}
                  />
                )}
              </Group>

              <MarketTabs value={market} onChange={setMarket} counts={counts} />
            </Stack>
          </Box>

          <ShipmentTable
            shipments={shipments}
            loading={shipmentsLoading}
            onSelect={handleSelectShipment}
            selectedId={detailOpened ? selectedShipment?.id : undefined}
            filter={filter}
            viewMode={viewMode}
          />
        </Container>

        <ShipmentDetail
          shipment={selectedShipment}
          opened={detailOpened}
          onClose={handleCloseDetail}
          onPrev={handlePrevShipment}
          onNext={handleNextShipment}
          hasPrev={hasPrev}
          hasNext={hasNext}
        />

        <HolidaySettings
          opened={settingsOpened}
          onClose={() => setSettingsOpened(false)}
        />

        <ReportLog
          opened={reportsOpened}
          onClose={() => setReportsOpened(false)}
        />
      </AppShell.Main>
    </AppShell>
  );
}

export default function App() {
  const { isAuthenticated, loading, error, signInWithGoogle } = useAuth();

  if (loading) {
    return (
      <Center h="100vh">
        <Loader size="lg" />
      </Center>
    );
  }

  if (!isAuthenticated) {
    return <LoginForm onGoogleSignIn={signInWithGoogle} loading={loading} error={error} />;
  }

  return <Dashboard />;
}
